import React, {useState} from 'react';
import TreeView from '@material-ui/lab/TreeView';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import TreeItem from '@material-ui/lab/TreeItem';
import {makeStyles} from '@material-ui/core/styles';
import OrganizationUnitTreeAdd from "./OrganizationUnitTreeAdd";
import OrganizationUnitTreeEdit from "./OrganizationUnitTreeEdit";
import OrganizationUnitTreeView from "./OrganizationUnitTreeView";
import {useApiNotification} from "../ui/ApiNotificationProvider";
import {fetchUserCapabilities} from "../../api/user-capability-functions";
import {useCapability} from "../../auth/CapabilityContext";
import {getOrganizationUnitChildren} from "../../api/organization-unit-functions";
import {OrganizationUnit} from "../../models/organization";

const useStyles = makeStyles({
    root: {
        height: 'auto',
        flexGrow: 1,
        maxWidth: 700,
    },
    textField: {
        padding: '8px'
    }
});


interface OrganizationUnitTreeItemProps {
    disableDelete?: boolean;
    unit: OrganizationUnit;
    onUnitEdited?: (unit: OrganizationUnit) => void
    onUnitDeleted?: () => void;
}

const OrganizationUnitTreeItem: React.FC<OrganizationUnitTreeItemProps> = (props) => {
    const classes = useStyles();
    const {handleError} = useApiNotification();
    const [unit, setUnit] = useState<OrganizationUnit>(props.unit);
    const [addMode, setAddMode] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [childNodes, setChildNodes] = useState<any[]>([]);
    const [expandedNodes, setExpandedNodes] = useState<any[]>([]);
    const [disableProcessingAction, setDisableProcessingAction] = useState(false);
    const {setCapabilities} = useCapability();

    const onNodeToggle = (event: any, nodeIds: any) => {
        const expanding = nodeIds.filter((id: any) => !expandedNodes.includes(id));
        setExpandedNodes(nodeIds);
        if (expanding[0])
            loadChildren();
    };

    const onAddHandler = () => {
        setAddMode(true);
        if (!expandedNodes.length)
            onNodeToggle(null, [unit.id.toString()]);
    };

    const onUnitAdded = (addedUnit: any) => {
        setChildNodes([...childNodes, addedUnit]);
        setAddMode(false);
    };

    const onUnitEdited = (editedUnit: any) => {
        setUnit(editedUnit);
        setEditMode(false);
        if (props.onUnitEdited)
            props.onUnitEdited(editedUnit);
    };

    const loadChildren = () => {
        getOrganizationUnitChildren(unit.organizationId, unit.id)
            .then((orgUnitChildren) => {
                if (orgUnitChildren)
                    setChildNodes(orgUnitChildren);
                else
                    throw orgUnitChildren;
                if (orgUnitChildren && !orgUnitChildren.length)
                    setAddMode(true);
            })
            .then(() => fetchUserCapabilities(unit.organizationId).then(setCapabilities))
            .catch(handleError);
    };

    const itemContent = !editMode ?
        <OrganizationUnitTreeView unit={unit} disableDelete={props.disableDelete}
                                  onEdit={() => setEditMode(true)}
                                  onAdd={onAddHandler} onDeleted={props.onUnitDeleted}
                                  disableProcessingAction={disableProcessingAction}
                                  onProcessingAction={setDisableProcessingAction}/>
        : <OrganizationUnitTreeEdit unit={unit}
                                    onCancel={() => setEditMode(false)}
                                    onEdited={onUnitEdited}/>;


    let children = childNodes.map(u => <OrganizationUnitTreeItem key={u.id.toString()} unit={u}
                                                                 onUnitDeleted={loadChildren}
                                                                 onUnitEdited={loadChildren}/>);
    if (addMode)
        children = [
            <OrganizationUnitTreeAdd key={unit.id + 'add'}
                                     unit={unit}
                                     onUnitAdded={onUnitAdded}
                                     onProcessingAction={setDisableProcessingAction}
                                     disableProcessingAction={disableProcessingAction}/>,
            ...children
        ];

    return (unit ?
        <TreeView className={classes.root} defaultCollapseIcon={<ExpandMoreIcon/>}
                  defaultExpandIcon={<ChevronRightIcon/>} onNodeToggle={onNodeToggle} expanded={expandedNodes}>
            <TreeItem key={unit.id} nodeId={unit.id.toString()} label={itemContent}>
                {[
                    <span key={unit.id.toString() + '-holder'}/>,
                    ...children
                ]}
            </TreeItem>
        </TreeView> : null);
};
export default OrganizationUnitTreeItem;
